import React from "react";
import { Check, X, ClipboardCheck, GitMerge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const ReviewPanel = ({ reviewItems, merges = [], user, onAction, onResolveMerge }) => (
  <div>
    <h2 className="font-serif text-2xl text-foreground mb-1">Review Queue</h2>
    <p className="text-sm text-muted-foreground mb-5">
      Changes submitted by curators. You can't approve your own — another curator or an admin must review it.
    </p>

    {merges.length > 0 && (
      <div className="mb-6" data-testid="merge-review-list">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
          <GitMerge className="w-4 h-4 text-primary" /> Pending merges ({merges.length})
        </div>
        <div className="space-y-3">
          {merges.map((m) => {
            const mine = m.proposed_by === user.email && user.role !== "admin";
            return (
              <div key={m.id} className="bg-card border border-border rounded-lg p-4" data-testid={`merge-review-${m.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{m.survivor_label}</span>
                      <Badge variant="outline" className="border-accent/40 text-accent">{m.type_label}</Badge>
                      <span className="text-[11px] uppercase tracking-wide font-semibold text-primary">merge</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      absorbing <b>{(m.duplicate_labels || []).join(", ")}</b> · proposed by {m.proposed_by || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={mine} onClick={() => onResolveMerge(m.id, "reject")} data-testid={`merge-reject-${m.id}`}>
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button size="sm" disabled={mine} onClick={() => onResolveMerge(m.id, "approve")} data-testid={`merge-approve-${m.id}`} className="bg-accent hover:bg-accent/90 text-accent-foreground">
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
                {mine && <div className="text-[11px] text-muted-foreground mt-2">You proposed this — awaiting another reviewer.</div>}
              </div>
            );
          })}
        </div>
      </div>
    )}

    {reviewItems.length === 0 ? (
      <div className="bg-card border border-border border-dashed rounded-lg p-10 text-center text-muted-foreground" data-testid="review-empty">
        <ClipboardCheck className="w-8 h-8 mx-auto mb-3 opacity-40" /> Nothing awaiting review.
      </div>
    ) : (
      <div className="space-y-3" data-testid="review-list">
        {reviewItems.map((it) => {
          const mine = it.submitted_by === user.email && user.role !== "admin";
          return (
            <div key={it.id} className="bg-card border border-border rounded-lg p-4" data-testid={`review-${it.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{it.label}</span>
                    <Badge variant="outline" className="border-accent/40 text-accent">{it.type_label}</Badge>
                    <span className="text-[11px] uppercase tracking-wide font-semibold text-primary">{it.action}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">submitted by {it.submitted_by || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={mine} onClick={() => onAction(it.id, "reject", { comment: "" })} data-testid={`reject-${it.id}`}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm" disabled={mine} onClick={() => onAction(it.id, "approve")} data-testid={`approve-${it.id}`} className="bg-accent hover:bg-accent/90 text-accent-foreground">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
              {mine && <div className="text-[11px] text-muted-foreground mt-2">You submitted this — awaiting another reviewer.</div>}
              {it.action === "update" && it.pending_data && (
                <div className="mt-3 text-xs bg-secondary/40 rounded-md p-3 border border-border">
                  <div className="font-semibold text-foreground mb-1">Proposed changes</div>
                  {Object.entries(it.pending_data).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground">{k}:</span> {Array.isArray(v) ? v.join(", ") : String(v)}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);
